import { useState, useEffect } from 'react';
import { ListChecks, Check, User, Clock, Calendar, AlertCircle, Timer } from 'lucide-react';
import { ViewChecklistModal } from './ViewChecklistModal';
import { getTaskChecklist } from '../../lib/checklists';
import { Task as TaskType } from '../../lib/tasks';

interface Props {
    task: TaskType;
    onStatusChange: (taskId: string, newStatus: string) => void;
    getResourceName: (resourceId: string | null) => string;
}

export default function Task({ task, onStatusChange, getResourceName }: Props) {
    const [showChecklistModal, setShowChecklistModal] = useState(false);
    const [hasChecklist, setHasChecklist] = useState(false);
    const [isChecklistComplete, setIsChecklistComplete] = useState(false);
    const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);

    useEffect(() => {
        checkForChecklist();
    }, [task.id]);

    const checkForChecklist = async () => {
        try {
            const checklist = await getTaskChecklist(task.id);
            console.log('Checklist found:', checklist); // Debug log
            setHasChecklist(!!checklist);
            if (checklist) {
                const allItemsCompleted = checklist.items.every(item => item.completion);
                setIsChecklistComplete(allItemsCompleted);
            }
        } catch (error) {
            console.error('Failed to check for checklist:', error);
        }
    };

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        console.log('Task clicked, has checklist:', hasChecklist); // Debug log
        setShowChecklistModal(true);
    };

    const handleStatusChange = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent opening checklist modal when clicking status button

        if (hasChecklist && !isChecklistComplete) {
            setShowIncompleteWarning(true);
            setTimeout(() => setShowIncompleteWarning(false), 3000); // Hide warning after 3 seconds
            return;
        }

        onStatusChange(task.id, task.status === 'pending' ? 'completed' : 'pending');
    };

    const handleChecklistUpdate = (isComplete: boolean) => {
        setIsChecklistComplete(isComplete);
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'No due date';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getPriorityColor = (priority: string) => {
        switch (priority.toLowerCase()) {
            case 'high':
                return 'bg-red-100 text-red-800';
            case 'medium':
                return 'bg-yellow-100 text-yellow-800';
            case 'low':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <>
            <div 
                className="bg-white rounded-lg shadow-sm hover:shadow-md border border-gray-200 transition-all duration-200 touch-manipulation"
                onClick={handleClick}
                role="button"
                tabIndex={0}
            >
                <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                                <h3 className="text-base font-semibold text-gray-900 truncate">
                                    {task.title}
                                </h3>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {hasChecklist && (
                                        <div className="flex items-center gap-1">
                                            <ListChecks className={`h-5 w-5 ${isChecklistComplete ? 'text-green-600' : 'text-blue-600'}`} />
                                            {showIncompleteWarning && (
                                                <div className="flex items-center text-xs text-red-600">
                                                    <AlertCircle className="h-4 w-4 mr-1" />
                                                    Complete checklist first
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {task.constraints?.disableClockOut && (
                                        <div className="relative group">
                                            <Timer className="h-5 w-5 text-red-600 flex-shrink-0" />
                                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                                Clock out restricted
                                            </div>
                                        </div>
                                    )}
                                    <span className={`px-2.5 py-1 text-sm font-medium rounded-full flex-shrink-0 ${getPriorityColor(task.priority)}`}>
                                        {task.priority}
                                    </span>
                                </div>
                            </div>
                            
                            {task.description && (
                                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                                    {task.description}
                                </p>
                            )}
                            
                            <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2.5 text-sm">
                                <div className="flex items-center text-gray-600 min-w-0">
                                    <User className="h-5 w-5 mr-2 flex-shrink-0 text-gray-500" />
                                    <span className="truncate">{getResourceName(task.resource_id || null)}</span>
                                </div>
                                
                                <div className="flex items-center text-gray-600">
                                    <Clock className="h-5 w-5 mr-2 flex-shrink-0 text-gray-500" />
                                    <span className="truncate">
                                        <span className="text-gray-500 mr-1">Start:</span>
                                        {formatDate(task.start_date)}
                                    </span>
                                </div>
                                
                                <div className="flex items-center text-gray-600">
                                    <Calendar className="h-5 w-5 mr-2 flex-shrink-0 text-gray-500" />
                                    <span className="truncate">
                                        <span className="text-gray-500 mr-1">Due:</span>
                                        {formatDate(task.due_date)}
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex-shrink-0">
                            {task.status !== 'completed' && (
                                <button
                                    onClick={handleStatusChange}
                                    className={`p-2.5 rounded-full transition-colors ${
                                        hasChecklist && !isChecklistComplete
                                            ? 'text-gray-300 hover:text-gray-400 cursor-not-allowed'
                                            : 'text-gray-400 hover:text-green-500 hover:bg-green-50'
                                    }`}
                                    title={hasChecklist && !isChecklistComplete ? 'Complete checklist first' : 'Mark as complete'}
                                >
                                    <Check className="h-6 w-6" />
                                </button>
                            )}
                        </div>
                    </div>

                    {task.is_recurring && (
                        <div className="mt-3">
                            <span className="inline-block px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-600">
                                Repeats {task.recurrence_frequency}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {showChecklistModal && (
                <ViewChecklistModal
                    taskId={task.id}
                    onClose={() => setShowChecklistModal(false)}
                    onChecklistUpdate={handleChecklistUpdate}
                />
            )}
        </>
    );
} 